// web/src/components/lifecycle/AddS3VaultWizard.tsx
//
// Post-master-unlock wizard for adding an S3-backed vault. Routed at
// `/vaults/add/:step`. Lives entirely client-side: every primitive
// it needs (probeS3, s3VaultExists, createS3Vault, openS3Vault,
// saveAppConfig, changePassphrase) is already exposed by the worker.
//
// Step 1 — connection details. Optional probe button.
// Step 2 — s3VaultExists branch: create vs import vs cancel.
// Step 3 — passphrase choice.
//   Create branch: synced (default, requires master re-entry) or
//                  unsynced (separate passphrase, zxcvbn ≥ 3 gate).
//   Import branch: vault's existing passphrase + sync vs keep choice.
// Step 4 — commit. Pipeline:
//   Create:
//     1. createS3Vault(cfg, vaultPass, displayName, false)
//     2. mint VaultEntry, append to appConfig.vaults, saveAppConfig
//     3. seed session store, navigate to /v/:id/~/
//   Import:
//     1. openS3Vault(cfg, currentPass)
//     2. if synced chosen: changePassphrase(handle, currentPass, masterPass)
//     3. mint VaultEntry, append to appConfig.vaults, saveAppConfig
//     4. seed session store, navigate to /v/:id/~/
//
// All passphrase Uint8Array buffers are zeroized in `finally`. React
// passphrase strings are cleared before navigation.
//
// UX concern: synced-create requires the user to retype the master
// password at step 3. The master plaintext is gone after unlock by
// design (master passphrase is never stored). Acceptable trade-off
// today; PR 5 may revisit with an "unlock the master for 30s" banner.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { zxcvbn } from "@zxcvbn-ts/core";
import { cn } from "../../lib/utils";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { useMasterSession } from "../../stores/masterSession";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import type {
  AppConfig,
  S3ConfigFull,
  UrlStyle,
  VaultEntry,
} from "../../worker/api";
import { PassphraseGenerator } from "../primitives/PassphraseGenerator";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";

interface S3FormState {
  displayName: string;
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  urlStyle: UrlStyle;
  accessKeyId: string;
  secretAccessKey: string;
}

const EMPTY_FORM: S3FormState = {
  displayName: "work",
  endpoint: "",
  region: "us-east-1",
  bucket: "",
  prefix: "",
  urlStyle: "path",
  accessKeyId: "",
  secretAccessKey: "",
};

function toS3Config(form: S3FormState): S3ConfigFull {
  return {
    endpoint: form.endpoint,
    region: form.region,
    bucket: form.bucket,
    prefix: form.prefix,
    urlStyle: form.urlStyle,
    accessKeyId: form.accessKeyId,
    secretAccessKey: form.secretAccessKey,
  };
}

type Existence = "unknown" | "exists" | "absent";

export function AddS3VaultWizard() {
  const { step: stepStr } = useParams();
  const step = stepStr ? Number(stepStr) : null;
  const navigate = useNavigate();
  const vault = useVault();
  const session = useSession((s) => s.session);
  const setSession = useSession((s) => s.setSession);
  const setAppConfig = useMasterSession((s) => s.setAppConfig);

  // Cancel returns to the caller — the currently-unlocked vault's
  // browser if there is one, otherwise the master unlock screen.
  const cancel = (): void => {
    if (session?.vaultId) {
      navigate(`/v/${session.vaultId}/~/`);
    } else {
      navigate("/master");
    }
  };

  const [form, setForm] = useState<S3FormState>(EMPTY_FORM);

  const [existence, setExistence] = useState<Existence>("unknown");
  const [detecting, setDetecting] = useState(false);

  // Step 3 / 4 state
  const [useSynced, setUseSynced] = useState(true);
  const [keepCurrentPassphrase, setKeepCurrentPassphrase] = useState(false);
  const [masterPass, setMasterPass] = useState("");
  const [vaultPass, setVaultPass] = useState("");
  const [importCurrentPass, setImportCurrentPass] = useState("");

  const [commitError, setCommitError] = useState<string | null>(null);

  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [step]);

  const clearPassphraseStrings = (): void => {
    setMasterPass("");
    setVaultPass("");
    setImportCurrentPass("");
  };

  // `/vaults/add` → `/vaults/add/1`.
  if (step === null) {
    return <Navigate to="/vaults/add/1" replace />;
  }

  // If step > 1 was reached without a populated form (direct URL nav,
  // refresh, etc.), bounce back to step 1.
  if (step > 1 && form.endpoint.length === 0) {
    return <Navigate to="/vaults/add/1" replace />;
  }

  const stepsBar = <StepsBar step={step} />;

  if (step === 1) {
    return (
      <Step1Connection
        form={form}
        setForm={setForm}
        firstFieldRef={firstFieldRef}
        probeS3={vault.probeS3}
        onCancel={cancel}
        onContinue={() => navigate("/vaults/add/2")}
        stepsBar={stepsBar}
      />
    );
  }

  if (step === 2) {
    return (
      <Step2Detect
        form={form}
        existence={existence}
        detecting={detecting}
        s3VaultExists={vault.s3VaultExists}
        onDetectStart={() => setDetecting(true)}
        onDetected={(present) => {
          setExistence(present ? "exists" : "absent");
          setDetecting(false);
        }}
        onDetectError={(msg) => {
          setCommitError(msg);
          setExistence("absent");
          setDetecting(false);
        }}
        onCancel={cancel}
        onContinue={() => navigate("/vaults/add/3")}
        stepsBar={stepsBar}
      />
    );
  }

  if (step === 3) {
    return (
      <Step3Passphrase
        isImport={existence === "exists"}
        useSynced={useSynced}
        setUseSynced={setUseSynced}
        keepCurrentPassphrase={keepCurrentPassphrase}
        setKeepCurrentPassphrase={setKeepCurrentPassphrase}
        masterPass={masterPass}
        setMasterPass={setMasterPass}
        vaultPass={vaultPass}
        setVaultPass={setVaultPass}
        importCurrentPass={importCurrentPass}
        setImportCurrentPass={setImportCurrentPass}
        onCancel={cancel}
        onContinue={() => navigate("/vaults/add/4")}
        stepsBar={stepsBar}
      />
    );
  }

  if (step === 4) {
    return (
      <Step4Commit
        vault={vault}
        cfg={toS3Config(form)}
        displayName={form.displayName}
        useSynced={useSynced}
        keepCurrentPassphrase={keepCurrentPassphrase}
        isImport={existence === "exists"}
        masterPass={masterPass}
        vaultPass={vaultPass}
        importCurrentPass={importCurrentPass}
        commitError={commitError}
        onCommitError={setCommitError}
        clearPassphraseStrings={clearPassphraseStrings}
        setSession={setSession}
        setAppConfig={setAppConfig}
        navigate={navigate}
        stepsBar={stepsBar}
      />
    );
  }

  return <Navigate to="/vaults/add/1" replace />;
}

// ============================================================
// Step components — extracted so the wizard's render dispatcher
// stays under SonarCloud's 15-cognitive-complexity ceiling and
// each step body owns its own keystroke-handling state.
// ============================================================

function StepsBar({ step }: { readonly step: number }) {
  return (
    <div className="mb-5 flex gap-1.5">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            "h-[3px] flex-1 rounded-full",
            i < step && "bg-accent-soft",
            i === step && "bg-accent",
            i > step && "bg-border",
          )}
        />
      ))}
    </div>
  );
}

interface Step1Props {
  readonly form: S3FormState;
  readonly setForm: React.Dispatch<React.SetStateAction<S3FormState>>;
  readonly firstFieldRef: React.RefObject<HTMLInputElement>;
  readonly probeS3: (cfg: S3ConfigFull) => Promise<{ outcome: string }>;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
  readonly stepsBar: React.ReactNode;
}

function Step1Connection({
  form,
  setForm,
  firstFieldRef,
  probeS3,
  onCancel,
  onContinue,
  stepsBar,
}: Step1Props) {
  const [probing, setProbing] = useState(false);
  const [probeOutcome, setProbeOutcome] = useState<string | null>(null);

  const onProbe = async (): Promise<void> => {
    setProbing(true);
    setProbeOutcome(null);
    try {
      const res = await probeS3(toS3Config(form));
      setProbeOutcome(res.outcome);
    } catch (err) {
      setProbeOutcome(
        `error: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setProbing(false);
    }
  };

  const canContinue = form.endpoint.length > 0 && form.bucket.length > 0;

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
      {stepsBar}
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Connect an S3-compatible vault
      </h1>
      <p className="mb-4 text-sm leading-relaxed text-text-soft">
        Enter the connection details for your S3-compatible bucket. The
        credentials are encrypted before being stored on disk.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
        className="space-y-3"
      >
        <Field
          id="add-s3-display"
          label="Display name"
          inputRef={firstFieldRef}
          value={form.displayName}
          onChange={(v) => setForm((f) => ({ ...f, displayName: v }))}
        />
        <Field
          id="add-s3-endpoint"
          label="Endpoint URL"
          value={form.endpoint}
          onChange={(v) => setForm((f) => ({ ...f, endpoint: v }))}
          placeholder="http://localhost:9000"
        />
        <Field
          id="add-s3-region"
          label="Region"
          value={form.region}
          onChange={(v) => setForm((f) => ({ ...f, region: v }))}
        />
        <Field
          id="add-s3-bucket"
          label="Bucket"
          value={form.bucket}
          onChange={(v) => setForm((f) => ({ ...f, bucket: v }))}
        />
        <Field
          id="add-s3-prefix"
          label="Prefix"
          value={form.prefix}
          onChange={(v) => setForm((f) => ({ ...f, prefix: v }))}
          placeholder="(empty for bucket root)"
        />

        <UrlStyleFieldset
          urlStyle={form.urlStyle}
          onChange={(s) => setForm((f) => ({ ...f, urlStyle: s }))}
        />

        <Field
          id="add-s3-access-key-id"
          label="Access key ID"
          value={form.accessKeyId}
          onChange={(v) => setForm((f) => ({ ...f, accessKeyId: v }))}
        />
        <Field
          id="add-s3-secret"
          label="Secret access key"
          type="password"
          value={form.secretAccessKey}
          onChange={(v) => setForm((f) => ({ ...f, secretAccessKey: v }))}
        />

        <div className="flex flex-wrap items-center gap-3 pt-1 text-[12px]">
          <button
            type="button"
            onClick={() => void onProbe()}
            disabled={probing || form.endpoint.length === 0}
            className="rounded-md border border-border px-3 py-1.5 text-text-soft disabled:opacity-40"
          >
            {probing ? "Probing…" : "Probe connection"}
          </button>
          {probeOutcome !== null && (
            <span className="text-text-soft">Probe: {probeOutcome}</span>
          )}
        </div>

        <FormActions
          onCancel={onCancel}
          submitDisabled={!canContinue}
          submitLabel="Continue →"
        />
      </form>
    </div>
  );
}

function UrlStyleFieldset({
  urlStyle,
  onChange,
}: {
  readonly urlStyle: UrlStyle;
  readonly onChange: (s: UrlStyle) => void;
}) {
  return (
    <fieldset className="rounded-md border border-border p-3">
      <legend className="px-1 text-[11px] uppercase tracking-[0.1em] text-text-dim">
        URL style
      </legend>
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="url-style"
            value="virtual-host"
            checked={urlStyle === "virtual-host"}
            onChange={() => onChange("virtual-host")}
          />
          {" "}
          <span>Virtual-host</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="url-style"
            value="path"
            checked={urlStyle === "path"}
            onChange={() => onChange("path")}
          />
          {" "}
          <span>Path</span>
        </label>
      </div>
    </fieldset>
  );
}

interface Step2Props {
  readonly form: S3FormState;
  readonly existence: Existence;
  readonly detecting: boolean;
  readonly s3VaultExists: (cfg: S3ConfigFull) => Promise<boolean>;
  readonly onDetectStart: () => void;
  readonly onDetected: (present: boolean) => void;
  readonly onDetectError: (msg: string) => void;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
  readonly stepsBar: React.ReactNode;
}

function Step2Detect({
  form,
  existence,
  detecting,
  s3VaultExists,
  onDetectStart,
  onDetected,
  onDetectError,
  onCancel,
  onContinue,
  stepsBar,
}: Step2Props) {
  // Kick off detection on first mount when state is "unknown". Done
  // in useEffect so the setState calls don't fire during render
  // (which warns about parent updates during child render). The
  // parent owns `existence` so we re-render with the new state and
  // this effect's guard short-circuits.
  useEffect(() => {
    if (existence !== "unknown" || detecting) return;
    onDetectStart();
    void (async () => {
      try {
        const present = await s3VaultExists(toS3Config(form));
        onDetected(present);
      } catch (err) {
        onDetectError(
          `Detection failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existence, detecting]);

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 md:px-6 md:py-12">
      {stepsBar}
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Detecting vault…
      </h1>
      <Step2Body
        existence={existence}
        bucket={form.bucket}
        prefix={form.prefix}
        onCancel={onCancel}
        onContinue={onContinue}
      />
    </div>
  );
}

function Step2Body({
  existence,
  bucket,
  prefix,
  onCancel,
  onContinue,
}: {
  readonly existence: Existence;
  readonly bucket: string;
  readonly prefix: string;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
}) {
  if (existence === "unknown") {
    return (
      <div className="flex items-center gap-2 text-sm text-text-soft">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        Checking{" "}
        <span className="font-mono">
          {bucket}/{prefix}
        </span>
      </div>
    );
  }
  if (existence === "exists") {
    return (
      <Step2Branch
        message={
          <>
            An existing vaultwisp vault was found at{" "}
            <span className="font-mono">
              {bucket}/{prefix}
            </span>
            . Import it?
          </>
        }
        continueLabel="Import this vault"
        onCancel={onCancel}
        onContinue={onContinue}
      />
    );
  }
  return (
    <Step2Branch
      message={
        <>
          This location is empty. Create a new vault here at{" "}
          <span className="font-mono">
            {bucket}/{prefix}
          </span>
          ?
        </>
      }
      continueLabel="Create new vault"
      onCancel={onCancel}
      onContinue={onContinue}
    />
  );
}

function Step2Branch({
  message,
  continueLabel,
  onCancel,
  onContinue,
}: {
  readonly message: React.ReactNode;
  readonly continueLabel: string;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
}) {
  return (
    <>
      <p className="mb-4 text-sm leading-relaxed text-text-soft">{message}</p>
      <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-4 py-3 text-sm text-text-soft md:py-2"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="rounded-md bg-accent px-4 py-3 text-sm font-medium text-white md:py-2"
        >
          {continueLabel}
        </button>
      </div>
    </>
  );
}

interface Step3Props {
  readonly isImport: boolean;
  readonly useSynced: boolean;
  readonly setUseSynced: (b: boolean) => void;
  readonly keepCurrentPassphrase: boolean;
  readonly setKeepCurrentPassphrase: (b: boolean) => void;
  readonly masterPass: string;
  readonly setMasterPass: (s: string) => void;
  readonly vaultPass: string;
  readonly setVaultPass: (s: string) => void;
  readonly importCurrentPass: string;
  readonly setImportCurrentPass: (s: string) => void;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
  readonly stepsBar: React.ReactNode;
}

function Step3Passphrase(props: Step3Props) {
  const {
    isImport,
    useSynced,
    keepCurrentPassphrase,
    masterPass,
    vaultPass,
    importCurrentPass,
    onCancel,
    onContinue,
    stepsBar,
  } = props;

  const vaultPassScore = useMemo(
    () => (vaultPass.length > 0 ? zxcvbn(vaultPass).score : 0),
    [vaultPass],
  );
  const vaultPassStrong = vaultPassScore >= 3;

  const canContinue = importContinueEligible({
    isImport,
    importCurrentPass,
    useSynced,
    keepCurrentPassphrase,
    masterPass,
    vaultPassStrong,
  });

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 md:px-6 md:py-12">
      {stepsBar}
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Vault passphrase
      </h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
      >
        {isImport ? (
          <Step3ImportFields {...props} />
        ) : (
          <Step3CreateFields
            {...props}
            vaultPassStrong={vaultPassStrong}
          />
        )}

        <FormActions
          onCancel={onCancel}
          submitDisabled={!canContinue}
          submitLabel="Add vault"
        />
      </form>
    </div>
  );
}

function importContinueEligible(args: {
  isImport: boolean;
  importCurrentPass: string;
  useSynced: boolean;
  keepCurrentPassphrase: boolean;
  masterPass: string;
  vaultPassStrong: boolean;
}): boolean {
  const {
    isImport,
    importCurrentPass,
    useSynced,
    keepCurrentPassphrase,
    masterPass,
    vaultPassStrong,
  } = args;
  if (isImport) {
    if (importCurrentPass.length === 0) return false;
    if (useSynced && !keepCurrentPassphrase && masterPass.length === 0) {
      return false;
    }
    return true;
  }
  if (useSynced) return masterPass.length > 0;
  return vaultPassStrong;
}

function Step3ImportFields({
  keepCurrentPassphrase,
  setKeepCurrentPassphrase,
  masterPass,
  setMasterPass,
  importCurrentPass,
  setImportCurrentPass,
}: Step3Props) {
  return (
    <>
      <p className="mb-3 text-sm text-text-soft">
        Enter the passphrase that was used to create this vault.
      </p>
      <label
        htmlFor="add-import-current"
        className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
      >
        Current vault passphrase
      </label>
      <PassphraseInput
        id="add-import-current"
        autoCompleteMode="current-password"
        aria-label="Current vault passphrase"
        value={importCurrentPass}
        onChange={(e) => setImportCurrentPass(e.target.value)}
      />
      <SyncFieldset
        legend="Sync state"
        nameAttr="import-sync"
        firstChecked={!keepCurrentPassphrase}
        firstLabel="Match my master password after unlock"
        secondChecked={keepCurrentPassphrase}
        secondLabel="Keep the vault's current passphrase (unsynced)"
        onFirstSelect={() => setKeepCurrentPassphrase(false)}
        onSecondSelect={() => setKeepCurrentPassphrase(true)}
      />
      {!keepCurrentPassphrase && (
        <MasterPassField
          masterPass={masterPass}
          setMasterPass={setMasterPass}
        />
      )}
    </>
  );
}

function Step3CreateFields({
  useSynced,
  setUseSynced,
  masterPass,
  setMasterPass,
  vaultPass,
  setVaultPass,
  vaultPassStrong,
}: Step3Props & { vaultPassStrong: boolean }) {
  return (
    <>
      <p className="mb-3 text-sm text-text-soft">
        Choose how this vault is unlocked.
      </p>
      <SyncFieldset
        legend="Sync state"
        nameAttr="create-sync"
        firstChecked={useSynced}
        firstLabel="Sync with master password (recommended)"
        secondChecked={!useSynced}
        secondLabel="Use a separate passphrase for this vault"
        onFirstSelect={() => setUseSynced(true)}
        onSecondSelect={() => setUseSynced(false)}
      />
      {useSynced && (
        <>
          <MasterPassField
            masterPass={masterPass}
            setMasterPass={setMasterPass}
          />
          <p className="mt-1.5 text-[12px] text-text-dim">
            Re-enter to authorize. Master password is never cached.
          </p>
        </>
      )}
      {!useSynced && (
        <UnsyncedPassPicker
          vaultPass={vaultPass}
          setVaultPass={setVaultPass}
          vaultPassStrong={vaultPassStrong}
        />
      )}
    </>
  );
}

function UnsyncedPassPicker({
  vaultPass,
  setVaultPass,
  vaultPassStrong,
}: {
  readonly vaultPass: string;
  readonly setVaultPass: (s: string) => void;
  readonly vaultPassStrong: boolean;
}) {
  const [useCustomPass, setUseCustomPass] = useState(false);
  return (
    <div className="mt-4">
      {!useCustomPass && (
        <>
          <PassphraseGenerator onChoose={setVaultPass} />
          {vaultPass.length > 0 && (
            <div className="mt-2 text-[12px] text-text-soft">
              Selected:{" "}
              <span className="font-mono text-text">{vaultPass}</span>
            </div>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => {
                setVaultPass("");
                setUseCustomPass(true);
              }}
              className="text-[12px] text-accent-soft underline hover:text-text"
            >
              Type my own passphrase
            </button>
          </div>
        </>
      )}
      {useCustomPass && (
        <>
          <label
            htmlFor="add-vault-pass"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Passphrase
          </label>
          <PassphraseInput
            id="add-vault-pass"
            autoCompleteMode="new-password"
            aria-label="Passphrase"
            value={vaultPass}
            onChange={(e) => setVaultPass(e.target.value)}
          />
          <PassphraseStrength passphrase={vaultPass} />
          {!vaultPassStrong && vaultPass.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              Too weak — use 5+ random words.
            </div>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => {
                setVaultPass("");
                setUseCustomPass(false);
              }}
              className="text-[12px] text-accent-soft underline hover:text-text"
            >
              Use the generator instead
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MasterPassField({
  masterPass,
  setMasterPass,
}: {
  readonly masterPass: string;
  readonly setMasterPass: (s: string) => void;
}) {
  return (
    <>
      <label
        htmlFor="add-master"
        className="mb-1 mt-4 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
      >
        Master passphrase
      </label>
      <PassphraseInput
        id="add-master"
        autoCompleteMode="current-password"
        aria-label="Master passphrase"
        value={masterPass}
        onChange={(e) => setMasterPass(e.target.value)}
      />
    </>
  );
}

function SyncFieldset({
  legend,
  nameAttr,
  firstChecked,
  firstLabel,
  secondChecked,
  secondLabel,
  onFirstSelect,
  onSecondSelect,
}: {
  readonly legend: string;
  readonly nameAttr: string;
  readonly firstChecked: boolean;
  readonly firstLabel: string;
  readonly secondChecked: boolean;
  readonly secondLabel: string;
  readonly onFirstSelect: () => void;
  readonly onSecondSelect: () => void;
}) {
  return (
    <fieldset className="mt-4 rounded-md border border-border p-3">
      <legend className="px-1 text-[11px] uppercase tracking-[0.1em] text-text-dim">
        {legend}
      </legend>
      <div className="space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={nameAttr}
            checked={firstChecked}
            onChange={onFirstSelect}
          />
          <span>{firstLabel}</span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={nameAttr}
            checked={secondChecked}
            onChange={onSecondSelect}
          />
          <span>{secondLabel}</span>
        </label>
      </div>
    </fieldset>
  );
}

function FormActions({
  onCancel,
  submitDisabled,
  submitLabel,
}: {
  readonly onCancel: () => void;
  readonly submitDisabled: boolean;
  readonly submitLabel: string;
}) {
  return (
    <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
      <button
        type="button"
        onClick={onCancel}
        className="w-full rounded-md border border-border px-4 py-3 text-sm text-text-soft md:w-auto md:py-2"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitDisabled}
        className="w-full rounded-md bg-accent px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:w-auto md:py-2"
      >
        {submitLabel}
      </button>
    </div>
  );
}

interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly placeholder?: string;
  readonly type?: "text" | "password";
  readonly inputRef?: React.RefObject<HTMLInputElement>;
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputRef,
}: FieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
      >
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-md border border-border bg-white/5 px-3 py-2.5 font-mono text-sm text-text placeholder:text-text-dim focus:outline-none focus:ring-1 focus:ring-accent md:py-2"
      />
    </div>
  );
}

interface VaultApiSubset {
  createS3Vault: (
    cfg: S3ConfigFull,
    passphrase: Uint8Array,
    displayName: string,
    wipeExisting: boolean,
  ) => Promise<number>;
  openS3Vault: (cfg: S3ConfigFull, passphrase: Uint8Array) => Promise<number>;
  saveAppConfig: (handle: number, cfg: AppConfig) => Promise<void>;
  changePassphrase: (
    handle: number,
    oldPass: Uint8Array,
    newPass: Uint8Array,
  ) => Promise<void>;
}

interface Step4Props {
  readonly vault: VaultApiSubset;
  readonly cfg: S3ConfigFull;
  readonly displayName: string;
  readonly useSynced: boolean;
  readonly keepCurrentPassphrase: boolean;
  readonly isImport: boolean;
  readonly masterPass: string;
  readonly vaultPass: string;
  readonly importCurrentPass: string;
  readonly commitError: string | null;
  readonly onCommitError: (msg: string | null) => void;
  readonly clearPassphraseStrings: () => void;
  readonly setSession: (s: {
    handle: number;
    vaultId: string;
    displayName: string;
    unlockedAt: number;
  }) => void;
  readonly setAppConfig: (cfg: AppConfig) => void;
  readonly navigate: (path: string) => void;
  readonly stepsBar: React.ReactNode;
}

function Step4Commit(props: Step4Props) {
  const startedRef = useRef(false);
  const {
    vault,
    cfg,
    displayName,
    useSynced,
    keepCurrentPassphrase,
    isImport,
    masterPass,
    vaultPass,
    importCurrentPass,
    commitError,
    onCommitError,
    clearPassphraseStrings,
    setSession,
    setAppConfig,
    navigate,
    stepsBar,
  } = props;

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      try {
        const newEntry = isImport
          ? await runImport({
              vault,
              cfg,
              displayName,
              keepCurrentPassphrase,
              masterPass,
              importCurrentPass,
            })
          : await runCreate({
              vault,
              cfg,
              displayName,
              useSynced,
              masterPass,
              vaultPass,
            });

        const masterState = useMasterSession.getState().master;
        if (!masterState?.appConfig) {
          throw new Error("Master session lost");
        }
        const updated: AppConfig = {
          ...masterState.appConfig,
          vaults: [...masterState.appConfig.vaults, newEntry.entry],
        };
        await vault.saveAppConfig(masterState.masterHandle, updated);
        setAppConfig(updated);

        setSession({
          handle: newEntry.handle,
          vaultId: newEntry.entry.id,
          displayName: newEntry.entry.display_name,
          unlockedAt: Date.now(),
        });

        clearPassphraseStrings();
        navigate(`/v/${newEntry.entry.id}/~/`);
      } catch (err) {
        onCommitError(err instanceof Error ? err.message : String(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 md:px-6 md:py-12">
      {stepsBar}
      {commitError === null ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="mb-3 h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          <div className="text-sm text-text-soft">Adding vault…</div>
        </div>
      ) : (
        <div className="rounded-lg border border-danger/40 bg-danger/10 p-4">
          <div className="mb-2 text-sm font-semibold text-danger">
            Could not add vault
          </div>
          <p className="mb-3 text-[13px] leading-relaxed text-text">
            {commitError}
          </p>
          <Link
            to="/vaults/add/1"
            className="text-[13px] text-accent-soft underline hover:text-text"
          >
            ← Start over
          </Link>
        </div>
      )}
    </div>
  );
}

// S3 credentials are persisted in the encrypted app-config for both
// synced AND unsynced vaults — that's the whole point of the
// app-config existing. `sync_state` governs only the VAULT
// PASSWORD: "synced" vaults inherit the master password,
// "unsynced" vaults have their own passphrase that is not stored
// and must be retyped every unlock. See `crates/vault-wasm/src/s3.rs`
// module docs and `docs/superpowers/specs/2026-04-23-master-password-design.md`
// for the full spec.
function buildVaultEntry(
  cfg: S3ConfigFull,
  displayName: string,
  syncState: "synced" | "unsynced",
): VaultEntry {
  return {
    id: `v_${crypto.randomUUID()}`,
    display_name: displayName,
    adapter: {
      type: "s3-compat",
      endpoint: cfg.endpoint,
      region: cfg.region,
      bucket: cfg.bucket,
      prefix: cfg.prefix,
      url_style: cfg.urlStyle === "virtual-host" ? "virtual-hosted" : "path",
      access_key_id: cfg.accessKeyId,
      secret_access_key: cfg.secretAccessKey,
    },
    sync_state: syncState,
    pending_rotation: null,
    last_opened_at: Math.floor(Date.now() / 1000),
  };
}

interface RunCreateArgs {
  vault: VaultApiSubset;
  cfg: S3ConfigFull;
  displayName: string;
  useSynced: boolean;
  masterPass: string;
  vaultPass: string;
}

async function runCreate(
  args: RunCreateArgs,
): Promise<{ entry: VaultEntry; handle: number }> {
  const { vault, cfg, displayName, useSynced, masterPass, vaultPass } = args;
  const passStr = useSynced ? masterPass : vaultPass;
  const bytes = toPassphraseBytes(passStr);
  try {
    const handle = await vault.createS3Vault(cfg, bytes, displayName, false);
    const entry = buildVaultEntry(
      cfg,
      displayName,
      useSynced ? "synced" : "unsynced",
    );
    return { entry, handle };
  } finally {
    zeroize(bytes);
  }
}

interface RunImportArgs {
  vault: VaultApiSubset;
  cfg: S3ConfigFull;
  displayName: string;
  keepCurrentPassphrase: boolean;
  masterPass: string;
  importCurrentPass: string;
}

async function runImport(
  args: RunImportArgs,
): Promise<{ entry: VaultEntry; handle: number }> {
  const { vault, cfg, displayName, keepCurrentPassphrase, masterPass, importCurrentPass } =
    args;
  const oldBytes = toPassphraseBytes(importCurrentPass);
  let masterBytes: Uint8Array | null = null;
  try {
    const handle = await vault.openS3Vault(cfg, oldBytes);
    if (!keepCurrentPassphrase) {
      // Re-wrap the keybag under the master password.
      masterBytes = toPassphraseBytes(masterPass);
      await vault.changePassphrase(handle, oldBytes, masterBytes);
    }
    const entry = buildVaultEntry(
      cfg,
      displayName,
      keepCurrentPassphrase ? "unsynced" : "synced",
    );
    return { entry, handle };
  } finally {
    zeroize(oldBytes);
    if (masterBytes) zeroize(masterBytes);
  }
}
